import React from 'react';
import styled from 'styled-components';
import Card from 'components/Card';
import ComponentDemo from '../../components/ComponentDemo';
import CodeBlock from '../../components/CodeBlock';

const PageHeader = styled.div`
  margin-bottom: 48px;
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 12px 0;
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin: 0;
  line-height: 1.6;
`;

const Section = styled.section`
  margin-bottom: 64px;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 8px 0;
`;

const SectionDescription = styled.p`
  font-size: 15px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  line-height: 1.7;
  margin: 0 0 32px 0;
`;

// Demo components
const DemoBox = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(250, 70, 22, 0.15)'
      : 'rgba(250, 70, 22, 0.08)'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(250, 70, 22, 0.3)' : '#FA4616'};
  border-radius: 8px;
  padding: 16px;
  text-align: center;
  color: ${(props) => (props.theme.name === 'dark' ? '#FA4616' : '#FA4616')};
  font-size: 14px;
  font-weight: 600;
  min-height: ${(props) => props.height || '80px'};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DemoGrid = styled.div`
  display: grid;
  grid-template-columns: ${(props) => props.columns || 'repeat(3, 1fr)'};
  gap: ${(props) => props.gap || '24px'};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const DemoFlex = styled.div`
  display: flex;
  flex-direction: ${(props) => props.direction || 'row'};
  gap: ${(props) => props.gap || '16px'};
  align-items: ${(props) => props.align || 'stretch'};
  justify-content: ${(props) => props.justify || 'flex-start'};
  flex-wrap: ${(props) => (props.wrap ? 'wrap' : 'nowrap')};

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const DemoContainer = styled.div`
  max-width: ${(props) => props.maxWidth || '1400px'};
  margin: 0 auto;
  padding: ${(props) => props.padding || '48px'};
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255,255,255,0.02)' : '#F8FAFC'};
  border-radius: 8px;

  @media (max-width: 768px) {
    padding: 24px 16px;
  }
`;

const Layout = () => {
  return (
    <div>
      <PageHeader>
        <Title>Layout</Title>
        <Subtitle>
          Layout patterns and utilities for organizing content consistently
          across the application. Built with CSS Grid and Flexbox for responsive
          designs.
        </Subtitle>
      </PageHeader>

      <Section>
        <SectionTitle>Container</SectionTitle>
        <SectionDescription>
          Containers provide consistent max-width and padding for page content.
        </SectionDescription>
        <ComponentDemo label="Page Container" compact>
          <DemoContainer maxWidth="600px" padding="24px">
            <DemoBox height="60px">Container Content (max-width: 1400px)</DemoBox>
          </DemoContainer>
        </ComponentDemo>
        <CodeBlock>
{`const Container = styled.div\`
  max-width: 1400px;
  margin: 0 auto;
  padding: 48px;

  @media (max-width: 768px) {
    padding: 24px 16px;
  }
\`;

// Usage
<Container>
  <YourContent />
</Container>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Grid Layout</SectionTitle>
        <SectionDescription>
          CSS Grid provides powerful layout capabilities for creating complex,
          responsive designs.
        </SectionDescription>

        <ComponentDemo label="3-Column Grid" compact>
          <DemoGrid columns="repeat(3, 1fr)" gap="24px">
            <DemoBox>Column 1</DemoBox>
            <DemoBox>Column 2</DemoBox>
            <DemoBox>Column 3</DemoBox>
          </DemoGrid>
        </ComponentDemo>

        <ComponentDemo label="2-Column Grid" compact>
          <DemoGrid columns="repeat(2, 1fr)" gap="24px">
            <DemoBox>Column 1</DemoBox>
            <DemoBox>Column 2</DemoBox>
          </DemoGrid>
        </ComponentDemo>

        <ComponentDemo label="Auto-Fill Responsive Grid" compact>
          <DemoGrid columns="repeat(auto-fill, minmax(200px, 1fr))" gap="24px">
            <DemoBox>Item 1</DemoBox>
            <DemoBox>Item 2</DemoBox>
            <DemoBox>Item 3</DemoBox>
            <DemoBox>Item 4</DemoBox>
          </DemoGrid>
        </ComponentDemo>

        <CodeBlock>
{`// 3-column grid
const Grid = styled.div\`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
\`;

// Auto-fill responsive grid
const ResponsiveGrid = styled.div\`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 24px;
\`;

// Usage from Customers page (MetricsGrid)
const MetricsGrid = styled.div\`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 20px;
  margin-bottom: 30px;

  @media (max-width: 1400px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
\`;`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Flexbox Layouts</SectionTitle>
        <SectionDescription>
          Flexbox is ideal for one-dimensional layouts and component alignment.
        </SectionDescription>

        <ComponentDemo label="Horizontal Flex (Row)" compact>
          <DemoFlex direction="row" gap="16px">
            <DemoBox>Item 1</DemoBox>
            <DemoBox>Item 2</DemoBox>
            <DemoBox>Item 3</DemoBox>
          </DemoFlex>
        </ComponentDemo>

        <ComponentDemo label="Vertical Flex (Column)" compact>
          <DemoFlex direction="column" gap="16px">
            <DemoBox>Item 1</DemoBox>
            <DemoBox>Item 2</DemoBox>
            <DemoBox>Item 3</DemoBox>
          </DemoFlex>
        </ComponentDemo>

        <ComponentDemo label="Space Between" compact>
          <DemoFlex direction="row" justify="space-between">
            <DemoBox>Left</DemoBox>
            <DemoBox>Right</DemoBox>
          </DemoFlex>
        </ComponentDemo>

        <ComponentDemo label="Centered" compact>
          <DemoFlex direction="row" justify="center" align="center">
            <DemoBox>Centered Content</DemoBox>
          </DemoFlex>
        </ComponentDemo>

        <CodeBlock>
{`// Horizontal layout
const Flex = styled.div\`
  display: flex;
  gap: 16px;
  align-items: center;
\`;

// Vertical stack
const Stack = styled.div\`
  display: flex;
  flex-direction: column;
  gap: 16px;
\`;

// Space between (header pattern)
const Header = styled.div\`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
\`;

// Centered
const CenteredContainer = styled.div\`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
\`;`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Sidebar Layout</SectionTitle>
        <SectionDescription>
          Common pattern for main content with a sidebar. Used in the Customers
          page.
        </SectionDescription>

        <ComponentDemo label="Sidebar Layout" compact>
          <div style={{ display: 'flex', gap: '24px', width: '100%' }}>
            <div style={{ flex: '1' }}>
              <DemoBox height="200px">Main Content</DemoBox>
            </div>
            <div style={{ width: '300px' }}>
              <DemoBox height="200px">Sidebar</DemoBox>
            </div>
          </div>
        </ComponentDemo>

        <CodeBlock>
{`// From Customers page
const DashboardLayout = styled.div\`
  display: flex;
  gap: 30px;
  align-items: flex-start;

  @media (max-width: 1200px) {
    flex-direction: column;
  }
\`;

const MainContent = styled.div\`
  flex: 1;
  min-width: 0;
\`;

const Sidebar = styled.aside\`
  width: 350px;
  position: sticky;
  top: 24px;

  @media (max-width: 1200px) {
    width: 100%;
    position: static;
  }
\`;

// Usage
<DashboardLayout>
  <MainContent>
    {/* Tables, lists, etc */}
  </MainContent>
  <Sidebar>
    {/* Quick actions, filters, etc */}
  </Sidebar>
</DashboardLayout>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Responsive Breakpoints</SectionTitle>
        <SectionDescription>
          Standard breakpoints used throughout the application for responsive
          design.
        </SectionDescription>

        <Card title="Breakpoint Reference" headerBorder>
          <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
            <div style={{ marginBottom: '12px' }}>
              <strong>Mobile:</strong> max-width: 768px
            </div>
            <div style={{ marginBottom: '12px' }}>
              <strong>Tablet:</strong> 768px - 1024px
            </div>
            <div style={{ marginBottom: '12px' }}>
              <strong>Desktop:</strong> 1024px - 1400px
            </div>
            <div>
              <strong>Large Desktop:</strong> min-width: 1400px
            </div>
          </div>
        </Card>

        <CodeBlock>
{`// Mobile First Approach
const Component = styled.div\`
  padding: 16px; // Mobile default

  @media (min-width: 768px) {
    padding: 24px; // Tablet
  }

  @media (min-width: 1024px) {
    padding: 32px; // Desktop
  }

  @media (min-width: 1400px) {
    padding: 48px; // Large desktop
  }
\`;

// Desktop First Approach (more common in this codebase)
const Component = styled.div\`
  padding: 48px; // Desktop default

  @media (max-width: 1024px) {
    padding: 32px; // Tablet
  }

  @media (max-width: 768px) {
    padding: 16px; // Mobile
  }
\`;`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Common Patterns</SectionTitle>
        <SectionDescription>
          Real-world layout patterns used in the application.
        </SectionDescription>

        <CodeBlock>
{`// Customers Page Layout
import styled from 'styled-components';

const Container = styled.div\`
  padding: 30px;
  max-width: 1800px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 20px 16px;
  }
\`;

const MetricsGrid = styled.div\`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 20px;
  margin-bottom: 30px;

  @media (max-width: 1400px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
\`;

const DashboardLayout = styled.div\`
  display: flex;
  gap: 30px;
  align-items: flex-start;

  @media (max-width: 1200px) {
    flex-direction: column;
  }
\`;

const MainContent = styled.div\`
  flex: 1;
  min-width: 0; // Prevents flex item overflow
\`;

const Sidebar = styled.aside\`
  width: 350px;

  @media (max-width: 1200px) {
    width: 100%;
  }
\`;

// Usage
<Container>
  <MetricsGrid>
    <AnimatedMetric />
    <AnimatedMetric />
    <AnimatedMetric />
  </MetricsGrid>

  <DashboardLayout>
    <MainContent>
      <CustomerTable />
    </MainContent>
    <Sidebar>
      <QuickActions />
      <SegmentBreakdown />
    </Sidebar>
  </DashboardLayout>
</Container>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Best Practices</SectionTitle>
        <ComponentDemo direction="column" align="stretch" gap="16px">
          <Card title="✅ Do" accentTitle>
            <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
              • Use CSS Grid for two-dimensional layouts (rows and columns)
              <br />
              • Use Flexbox for one-dimensional layouts (single row or column)
              <br />
              • Design mobile-first, then enhance for larger screens
              <br />
              • Use consistent spacing tokens from the theme
              <br />
              • Test layouts at all breakpoints
              <br />• Use min-width: 0 on flex items to prevent overflow
            </div>
          </Card>

          <Card title="❌ Don't" accentTitle>
            <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
              • Don't use fixed widths without responsive alternatives
              <br />
              • Don't nest grids too deeply (prefer flat structures)
              <br />
              • Don't forget to test on actual mobile devices
              <br />
              • Don't use arbitrary spacing values (use theme.spacing.*)
              <br />• Don't ignore horizontal scrolling on small screens
            </div>
          </Card>
        </ComponentDemo>
      </Section>
    </div>
  );
};

export default Layout;